import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
// types
import type { ComputedRef, Ref } from 'vue'
import type { BlockGradeTypeCardProps } from '@ts-blocks/block-grade-type-card/interfaces'
import type { BlockSectionLayoutProps } from '@ts-blocks/block-section-layout/interfaces'
import type { SectionGradeTypeProps } from '@ts-sections/section-grade-type/interfaces'

export type GradeTypeColor = 'sunshine' | 'orange' | 'ultraviolet'

const GRADE_COLORS: GradeTypeColor[] = ['sunshine', 'orange', 'ultraviolet']
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)'

interface SectionGradeTypeHookOptions {
  section: () => SectionGradeTypeProps
}

interface SectionGradeTypeHookReturn {
  refTitle: ComputedRef<string>
  refBackgroundColor: ComputedRef<NonNullable<BlockSectionLayoutProps['backgroundColor']>>
  refTrack: Ref<HTMLElement | null>
  refSticky: Ref<HTMLElement | null>
  refActiveIndex: Ref<number>
  getGradeColor: (index: number) => GradeTypeColor
  getImageAlt: (grade: BlockGradeTypeCardProps) => string
}

export const useSectionGradeType = ({ section }: SectionGradeTypeHookOptions): SectionGradeTypeHookReturn => {
  const refLayout = computed<BlockSectionLayoutProps | null>(() => section().layout ?? null)

  const refTitle = computed<string>(() => refLayout.value?.title ?? '')

  const refBackgroundColor = computed<NonNullable<BlockSectionLayoutProps['backgroundColor']>>(() => (
    refLayout.value?.backgroundColor ?? 'secondary'
  ))

  const refTrack = ref<HTMLElement | null>(null)
  const refSticky = ref<HTMLElement | null>(null)
  const refActiveIndex = ref(0)

  const getGradeColor = (index: number): GradeTypeColor => GRADE_COLORS[index % GRADE_COLORS.length] as GradeTypeColor

  const getImageAlt = (grade: BlockGradeTypeCardProps): string => (
    grade.image?.alternativeText ?? `${grade.titleLead}${grade.title}`
  )

  const updateActiveIndex = (): void => {
    const track = refTrack.value
    const sticky = refSticky.value
    if (!track || !sticky) return

    if (!window.matchMedia(DESKTOP_MEDIA_QUERY).matches) {
      refActiveIndex.value = 0
      return
    }

    const gradesCount = section().grades.length
    const scrollable = track.offsetHeight - sticky.offsetHeight
    if (scrollable <= 0 || gradesCount === 0) return

    const traveled = sticky.getBoundingClientRect().top - track.getBoundingClientRect().top
    const progress = Math.min(Math.max(traveled / scrollable, 0), 1)
    refActiveIndex.value = Math.min(gradesCount - 1, Math.floor(progress * gradesCount))
  }

  onMounted(() => {
    updateActiveIndex()
    window.addEventListener('scroll', updateActiveIndex, { passive: true })
    window.addEventListener('resize', updateActiveIndex, { passive: true })
  })

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', updateActiveIndex)
    window.removeEventListener('resize', updateActiveIndex)
  })

  return {
    refTitle,
    refBackgroundColor,
    refTrack,
    refSticky,
    refActiveIndex,
    getGradeColor,
    getImageAlt,
  }
}
